import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Notify, Confirm, Loading } from 'notiflix';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Pagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  AddCircle,
  Settings
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import DataTable from 'examples/Tables/DataTable';
import {
  clearError,
  deleteValuePack,
  selectLoading,
  selectSubmitting,
  selectError,
  selectCurrentOperation,
  fetchValuePackList,
  selectValuePacksWithCategoryNames,
  selectPagination,
  setCurrentPage,
  updateCommonValuePackData
} from 'store/slices/valuePackSlice';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const ITEMS_PER_PAGE = 10;

const ValuePackList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canCreate = usePermission('shop-management', 'create');
  const canRead = usePermission('shop-management', 'read');
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');

  const valuePacks = useSelector(selectValuePacksWithCategoryNames);
  const pagination = useSelector(selectPagination);
  const loading = useSelector(selectLoading);
  const submitting = useSelector(selectSubmitting);
  const error = useSelector(selectError);
  const currentOperation = useSelector(selectCurrentOperation);
  
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState('');
  const [commonDataDialogOpen, setCommonDataDialogOpen] = useState(false);
  const [commonData, setCommonData] = useState({
    heroTitle: 'Premium Value Packs',
    heroSubtitle: 'Discover our comprehensive solutions for your business',
    defaultDescription: 'Our value packs are designed to provide the best solutions for your needs',
    termsAndConditions: '',
    refundPolicy: '',
    supportEmail: 'support@yourcompany.com',
    supportPhone: '+1-800-123-4567'
  });

  useEffect(() => {
    // Fetch the current page (persisted in Redux, so returning to this list
    // after viewing/editing a pack keeps you on the same page instead of
    // resetting to page 1).
    if (!canRead) return;

    const fetchData = async () => {
      try {
        await dispatch(fetchValuePackList({
          page: pagination.currentPage || 1,
          limit: ITEMS_PER_PAGE,
        }));
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, [dispatch, pagination.currentPage, canRead]);

  // The page number is persisted (redux-persist), so it can point past the
  // last page once packs are deleted or the API points at another database -
  // the list then came back empty with no pager to get out. Go to the last page.
  useEffect(() => {
    if (!loading && pagination.totalPages > 0 && pagination.currentPage > pagination.totalPages) {
      dispatch(setCurrentPage(pagination.totalPages));
    }
  }, [dispatch, loading, pagination.currentPage, pagination.totalPages]);

  const handlePageChange = (event, newPage) => {
    dispatch(setCurrentPage(newPage));
  };

  useEffect(() => {
    if (error) {
      Notify.failure(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleDelete = async (id) => {
    Confirm.show(
      'Delete Value Pack',
      'Are you sure you want to delete this value pack? This action cannot be undone.',
      'Delete',
      'Cancel',
      async () => {
        setDeletingId(id);
        Loading.circle('Deleting value pack...');

        try {
          const pack = (Array.isArray(valuePacks) ? valuePacks : []).find(p => p.id === id);
          await dispatch(deleteValuePack({ id, label: pack?.name || '' })).unwrap();
          Notify.success('Value pack deleted successfully');
        } catch (error) {
          Notify.failure(error || 'Failed to delete value pack');
        } finally {
          Loading.remove();
          setDeletingId(null);
        }
      },
      {
        okButtonBackground: '#ff6b6b',
        titleColor: '#ff6b6b',
      }
    );
  };

  const handleView = (id) => {
    navigate(`/value-pack/view/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/value-pack/edit/${id}`);
  };

  const handleSaveCommonData = async () => {
    try {
      await dispatch(updateCommonValuePackData(commonData)).unwrap();
      Notify.success('Common data updated successfully');
      setCommonDataDialogOpen(false);
    } catch (error) {
      Notify.failure(error || 'Failed to update common data');
    }
  };

  const columns = [
    { Header: 'Name', accessor: 'name', width: '20%', align: 'left' },
    { Header: 'Description', accessor: 'description', width: '25%', align: 'left' },
    { Header: 'Categories', accessor: 'categories', width: '25%', align: 'left' },
    { Header: 'Status', accessor: 'status', width: '12%', align: 'center' },
    { Header: 'Created', accessor: 'created', width: '10%', align: 'center' },
    { Header: 'Actions', accessor: 'actions', width: '8%', align: 'center' },
  ];

  const filteredValuePacks = !search
    ? (Array.isArray(valuePacks) ? valuePacks : [])
    : (Array.isArray(valuePacks) ? valuePacks : []).filter(p =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.shortDescription?.toLowerCase().includes(search.toLowerCase())
      );

  const getRows = () => {
    return filteredValuePacks.map((pack) => {
      const isDeleting = deletingId === pack.id;
      
      return {
        name: (
          <Box>
            <MDTypography variant="caption" fontWeight="medium" color="text">
              {pack.name || 'Untitled'}
            </MDTypography>
            {pack.featured && (
              <Box mt={0.5}>
                <Chip 
                  label="Featured" 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        ),
        description: (
          <MDTypography variant="caption" color="text">
            {pack.shortDescription || 
             (pack.descriptionPreview ? pack.descriptionPreview + '...' : 'No description')}
          </MDTypography>
        ),
        categories: (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {pack.categoryNames && pack.categoryNames.length > 0 ? 
              pack.categoryNames.map((categoryName, index) => (
                <Chip 
                  key={index} 
                  label={categoryName} 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
              )) : (
                <MDTypography variant="caption" color="textSecondary">
                  No categories
                </MDTypography>
              )}
          </Box>
        ),
        status: (
          <MDTypography 
            variant="caption" 
            color={pack.active ? 'success' : 'error'}
            fontWeight="medium"
          >
            {pack.active ? 'Active' : 'Inactive'}
          </MDTypography>
        ),
        created: (
          <MDTypography variant="caption" color="text">
            {pack.createdAt ? moment(pack.createdAt).format('MMM D, YYYY') : 'N/A'}
          </MDTypography>
        ),
        actions: (
          <Box display="flex" gap={0.5} justifyContent="center">
            {canRead && (
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={() => handleView(pack.id)}
                  disabled={isDeleting}
                >
                  <VisibilityIcon color="info" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canUpdate && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(pack.id)}
                  disabled={isDeleting}
                >
                  <EditIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(pack.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon color="error" fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };
    });
  };

  const isInitialLoading = loading && (
    currentOperation === 'fetchValuePackList'
  );

  // Common Data Dialog
  // const CommonDataDialog = () => (
  //   <Dialog 
  //     open={commonDataDialogOpen} 
  //     onClose={() => setCommonDataDialogOpen(false)}
  //     maxWidth="md"
  //     fullWidth
  //   >
  //     <DialogTitle>Common Value Pack Settings</DialogTitle>
  //     <DialogContent>
  //       <Grid container spacing={2} sx={{ mt: 1 }}>
  //         <Grid item xs={12}>
  //           <TextField
  //             fullWidth
  //             label="Hero Title"
  //             value={commonData.heroTitle}
  //             onChange={(e) => setCommonData({...commonData, heroTitle: e.target.value})}
  //           />
  //         </Grid>
  //         <Grid item xs={12}>
  //           <TextField
  //             fullWidth
  //             label="Hero Subtitle"
  //             value={commonData.heroSubtitle}
  //             onChange={(e) => setCommonData({...commonData, heroSubtitle: e.target.value})}
  //             multiline
  //             rows={2}
  //           />
  //         </Grid>
  //         <Grid item xs={12}>
  //           <TextField
  //             fullWidth
  //             label="Default Description"
  //             value={commonData.defaultDescription}
  //             onChange={(e) => setCommonData({...commonData, defaultDescription: e.target.value})}
  //             multiline
  //             rows={3}
  //           />
  //         </Grid>
  //         <Grid item xs={12}>
  //           <TextField
  //             fullWidth
  //             label="Terms & Conditions"
  //             value={commonData.termsAndConditions}
  //             onChange={(e) => setCommonData({...commonData, termsAndConditions: e.target.value})}
  //             multiline
  //             rows={4}
  //           />
  //         </Grid>
  //         <Grid item xs={12}>
  //           <TextField
  //             fullWidth
  //             label="Refund Policy"
  //             value={commonData.refundPolicy}
  //             onChange={(e) => setCommonData({...commonData, refundPolicy: e.target.value})}
  //             multiline
  //             rows={3}
  //           />
  //         </Grid>
  //         <Grid item xs={12} md={6}>
  //           <TextField
  //             fullWidth
  //             label="Support Email"
  //             value={commonData.supportEmail}
  //             onChange={(e) => setCommonData({...commonData, supportEmail: e.target.value})}
  //           />
  //         </Grid>
  //         <Grid item xs={12} md={6}>
  //           <TextField
  //             fullWidth
  //             label="Support Phone"
  //             value={commonData.supportPhone}
  //             onChange={(e) => setCommonData({...commonData, supportPhone: e.target.value})}
  //           />
  //         </Grid>
  //       </Grid>
  //     </DialogContent>
  //     <DialogActions>
  //       <Button onClick={() => setCommonDataDialogOpen(false)}>Cancel</Button>
  //       <Button onClick={handleSaveCommonData} variant="contained">Save</Button>
  //     </DialogActions>
  //   </Dialog>
  // );

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to value packs. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  if (isInitialLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress />
            <Typography variant="h6" mt={2}>Loading value packs...</Typography>
          </Box>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Value Packs ({pagination.totalItems ?? (Array.isArray(valuePacks) ? valuePacks.length : 0)})
            </MDTypography>

            <Box mb={2} display="flex" justifyContent="flex-end" gap={2}>
              {/* <Button
                variant="contained"
                startIcon={<Settings />}
                onClick={() => navigate('/value-pack/common-settings')}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  },
                  '& .MuiButton-startIcon': {
                    color: '#fff'
                  }
                }}
              >
                Common Settings
              </Button> */}
              
              {canCreate && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/value-pack/create')}
                  startIcon={<AddCircle />}
                  disabled={submitting}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    },
                    '& .MuiButton-startIcon': {
                      color: '#fff'
                    }
                  }}
                >
                  Create New
                </Button>
              )}
            </Box>
          </MDBox>
          <MDBox pt={3}>
            <MDBox px={3} pb={2} display="flex" alignItems="center">
              <TextField
                placeholder="Search value packs…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                size="small"
                sx={{
                  width: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    background: '#f8fafc',
                    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                  },
                }}
                InputProps={{ startAdornment: <span style={{ marginRight: 6, color: '#94a3b8' }}>🔍</span> }}
              />
            </MDBox>
            {isInitialLoading ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                py={6}
                flexDirection="column"
              >
                <CircularProgress />
                <Typography variant="h6" mt={2}>
                  Loading value packs...
                </Typography>
              </Box>
            ) : !Array.isArray(valuePacks) || valuePacks.length === 0 ? (
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                py={6}
                flexDirection="column"
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No value packs found
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Create your first value pack to get started
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/value-pack/create')}
                  startIcon={<AddCircle />}
                  disabled={submitting}
                  sx={{ color: "#fff" }}
                >
                  Create Value Pack
                </Button>
              </Box>
            ) : (
              <>
                <DataTable
                  table={{ columns, rows: getRows() }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
                {pagination.totalPages > 1 && (
                  <Box display="flex" justifyContent="flex-end" py={3}>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.currentPage}
                      onChange={handlePageChange}
                      color="info"
                      shape="rounded"
                      disabled={loading}
                    />
                  </Box>
                )}
              </>
            )}
          </MDBox>
        </Card>

       {/* <CommonDataDialog /> */}
      </MDBox>
    </DashboardLayout>
  );
};

export default ValuePackList;
